/**
 * Auto-reload the app when a dynamic import fails because the chunk no
 * longer exists on the server (typical after a new deploy leaves the
 * user on a stale index.html that references old hashed filenames).
 *
 * Vite emits `vite:preloadError` for these failures. We reload once per
 * session to pick up the fresh index.html; sessionStorage guards against
 * an infinite reload loop if the failure is not actually stale-deploy.
 *
 * A plain reload is not enough when the chunk failed because a service-worker
 * cache holds the SPA HTML fallback under the chunk's .js URL (OWA-691): the
 * reload is served from that same poisoned cache and fails identically, which
 * is exactly the "chunk load failed again after recent reload" loop seen on
 * beta on 2026-07-23. So the FIRST recovery attempt purges poisoned cache
 * entries before reloading, and that purge gets its own cooldown — a client
 * that already reloaded (under the old code, or for an unrelated reason) still
 * gets the one attempt that can actually fix it.
 */
import {captureException} from './picos/error';
import {purgePoisonedAssets} from './cachePurge';

const RELOAD_FLAG = 'outlaw_chunk_reload_at';
const PURGE_FLAG = 'outlaw_chunk_purge_at';
const RELOAD_COOLDOWN_MS = 30_000;

const recently = (key: string, now: number): boolean => {
    const last = Number(sessionStorage.getItem(key) ?? '0');
    return Boolean(last) && now - last < RELOAD_COOLDOWN_MS;
};

export type RecoveryAction = 'purge-and-reload' | 'reload' | 'give-up';

/**
 * Which recovery attempt is still available. Split out from the side-effecting
 * path so every branch is testable — `reloadForStaleChunk` itself can only be
 * tested on the 'give-up' branch, since the others reload the test harness.
 */
export function nextRecoveryAction(now: number): RecoveryAction {
    if (!recently(PURGE_FLAG, now)) return 'purge-and-reload';
    if (!recently(RELOAD_FLAG, now)) return 'reload';
    return 'give-up';
}

export function isChunkLoadError(err: unknown): boolean {
    const message = err instanceof Error ? err.message : String(err ?? '');
    return (
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('Importing a module script failed') ||
        message.includes('error loading dynamically imported module')
    );
}

export function reloadForStaleChunk(err: unknown, source: string): boolean {
    const now = Date.now();
    const action = nextRecoveryAction(now);

    // First attempt: drop any cache entry holding HTML under a code-asset URL,
    // then reload. Without this the reload re-reads the same poisoned entry.
    // The purge is async but never rejects; we reload once it settles so the
    // fresh page cannot race the deletes.
    if (action === 'purge-and-reload') {
        console.warn(`[chunkReload] ${source}: chunk load failed, purging poisoned caches then reloading`, err);
        sessionStorage.setItem(PURGE_FLAG, String(now));
        sessionStorage.setItem(RELOAD_FLAG, String(now));
        void purgePoisonedAssets()
            .then(purged => {
                if (purged.length) console.warn(`[chunkReload] purged ${purged.length} poisoned cache entr${purged.length === 1 ? 'y' : 'ies'}`, purged);
            })
            .finally(() => window.location.reload());
        return true;
    }

    if (action === 'reload') {
        console.warn(`[chunkReload] ${source}: stale chunk detected, reloading`, err);
        sessionStorage.setItem(RELOAD_FLAG, String(now));
        window.location.reload();
        return true;
    }

    // Purged and reloaded already — don't loop. Let the error surface so the
    // user sees the error frame and can reach Support.
    console.error(`[chunkReload] ${source}: chunk load failed again after purge and reload`, err);
    captureException(err instanceof Error ? err : new Error(String(err)), {
        tags: ['chunkReload', 'post-reload-failure'],
        extra: {source},
    });
    return false;
}

export function installChunkReloadHandler(): void {
    window.addEventListener('vite:preloadError', (event) => {
        if (reloadForStaleChunk(event.payload, 'vite:preloadError')) {
            event.preventDefault();
        }
    });

    window.addEventListener('unhandledrejection', (event) => {
        if (isChunkLoadError(event.reason) && reloadForStaleChunk(event.reason, 'unhandledrejection')) {
            event.preventDefault();
        }
    });
}
